import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";

import { CallbackRail } from "@/components/CallbackRail";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { IMAGES, BBN_SECURITY } from "@/lib/images";

const PDF_40 = "/datablad/3m-safety-40-invandig.pdf";
const PDF_70 = "/datablad/3m-safety-70.pdf";

const FAQS: FaqItem[] = [
  {
    q: "Vad är kombifilm?",
    a: "3M:s kombifilm är säkerhetsfilm och solfilm i samma produkt. Du får skydd mot inbrott och splitter samt minskad värme och UV-strålning, med en gemensam installation och garanti i stället för två separata filmer.",
  },
  {
    q: "Blir solskyddet sämre av att det också är säkerhetsfilm?",
    a: "Nej. Kombiserien använder samma spektralt selektiva skikt som ren solfilm. Värmereduktionen ligger 70–80 % beroende på toning.",
  },
  {
    q: "När passar kombifilm bäst?",
    a: "Kombifilm passar när samma glas behöver både solskydd och säkerhetsfilm. Vanliga exempel är butiksskyltfönster mot söder, exponerade kontor och bilhallars showroom-glas.",
  },
  {
    q: "Kostnad jämfört med två separata filmer?",
    a: "En kombifilm kostar typiskt 20–30 % mer per m² än enbart solfilm, men 30–40 % mindre än att installera solfilm + säkerhetsfilm separat.",
  },
];

/** Ordagrant från gamla bbnnordic.se/3m-sakerhetsfilm-med-solfilm. */
const ULTRA_COMBI: { name: string; pdf?: string; features: string[] }[] = [
  {
    name: "3M Ultra Silver S20",
    features: [
      "Ultra Silver S20 är en säkerhetsfilm och samtidigt en effektiv solfilm",
      "Det är den mest värmeavvisande säkerhetsfilmen",
      "Reducerar kostnaderna för luftkonditionering",
      "Reducerar bländande strålar",
    ],
  },
  {
    name: "3M Ultra Night Vision S25",
    pdf: "/datablad/3m-ultra-night-vision-s25.pdf",
    features: [
      "Ultra NV S25 är en säkerhetsfilm som är kombinerad med 3M’s patenterade Night Vision-solfilm",
      "Det är en säkerhetsfilm som tonar fönsterrutan utan alltför mycket metallglans, så att den ger användaren bättre utsyn om kvällen och natten",
      "Reducerar kostnaderna för luftkonditionering",
      "Reducerar bländande strålar",
    ],
  },
  {
    name: "3M Ultra Prestige S70",
    pdf: "/datablad/3m-ultra-prestige-s70.pdf",
    features: [
      "Ultra Prestige S70 är en unik patenterad säkerhetsfilm",
      "Det är en otroligt effektiv solfilm som är så klar att man inte lägger märke till den på fönsterrutan",
      "Den är mycket värmeavvisande och används när man vill bevara ett oförändrat utseende utifrån och samtidigt släppa in så mycket solljus som möjligt",
      "Reducerar kostnaderna för luftkonditionering",
      "Reducerar bländande strålar",
    ],
  },
];

export const Route = createFileRoute("/3m-sakerhetsfilm-med-solfilm")({
  head: () => ({
    meta: [
      { title: "3M säkerhetsfilm med solfilm — kombifilm | BBN Nordic" },
      { name: "description", content: "Kombifilm från 3M — säkerhetsfilm och solfilm i samma film. Skydd mot inbrott och splitter + värmereduktion upp till 80 % och 99 % UV-blockering." },
      { property: "og:title", content: "3M säkerhetsfilm med solfilm" },
      { property: "og:description", content: "En film — inbrottsskydd, splitterskydd, solreduktion och UV-blockering." },
      { property: "og:type", content: "product" },
      { property: "og:url", content: "https://bbnnordic.se/3m-sakerhetsfilm-med-solfilm" },
      { property: "og:image", content: IMAGES.sakerhetsfilm },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/3m-sakerhetsfilm-med-solfilm" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "3M kombifilm — säkerhetsfilm med solfilm",
          category: "Fönsterfilm / Kombifilm (säkerhet + sol)",
          description:
            "3M kombifilm — säkerhetsfilm och solfilm i samma film. Skydd mot inbrott och splitter kombinerat med 70–80 % värmereduktion och 99 % UV-blockering.",
          image: IMAGES.sakerhetsfilm,
          brand: { "@type": "Brand", name: "3M" },
          manufacturer: { "@type": "Organization", name: "3M" },
          material: "Flerlagerlaminat med spektralt selektivt solskyddsskikt",
          url: "https://bbnnordic.se/3m-sakerhetsfilm-med-solfilm",
          additionalProperty: [
            { "@type": "PropertyValue", name: "Total solenergireduktion (TSER)", value: "70–80 %", unitText: "%" },
            { "@type": "PropertyValue", name: "UV-reduktion", value: "99 %", unitText: "%" },
            { "@type": "PropertyValue", name: "Standard", value: "EN 12600 säkerhetsklass" },
            { "@type": "PropertyValue", name: "Garanti", value: "10 år produktgaranti + 10 år arbetsgaranti" },
          ],
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "SEK",
            lowPrice: "1400",
            priceSpecification: { "@type": "PriceSpecification", price: "1400", priceCurrency: "SEK" },
            price: "1400",
            highPrice: "2400",
            availability: "https://schema.org/InStock",
            priceValidUntil: "2026-12-31",
            seller: { "@id": "https://bbnnordic.se/#organization" },
            areaServed: "SE",
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "SE",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "SEK" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "SE" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
              },
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "6",
            bestRating: "5",
            worstRating: "1",
          },
          review: [
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Andrée Johnsson" },
              reviewBody: "Kan varmt rekommenderas. Vi hade en undermålig solfilm som konkurrent satt upp, Marcus knackade på när han var hos grannen och sa att det där kan göras bättre.",
            },
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Christian Backe" },
              reviewBody: "Vi hade problem med att våra fönster släppte in väldigt mycket värme och valde en film som BBN satte med bästa resultat. Kan varmt rekommendera!",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://bbnnordic.se/" },
            { "@type": "ListItem", position: 2, name: "Säkerhetsfilm", item: "https://bbnnordic.se/sakerhetsfilm" },
            { "@type": "ListItem", position: 3, name: "Med solfilm", item: "https://bbnnordic.se/3m-sakerhetsfilm-med-solfilm" },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <ContentPage
      eyebrow="KOMBIFILM"
      title="3M säkerhetsfilm med solfilm"
      subtitle="Med kombifilm får du skydd mot inbrott och splitter samtidigt som filmen minskar värme och UV-strålning."
      heroImage={BBN_SECURITY[4]}
      breadcrumbs={[{ label: "Säkerhetsfilm", to: "/sakerhetsfilm" }, { label: "Med solfilm" }]}
      sections={[
        {
          eyebrow: "TVÅ FUNKTIONER, EN FILM",
          heading: "Så fungerar säkerhetsfilm med solfilm.",
          paragraphs: [
            "3M:s kombifilm har ett spektralt selektivt skikt som minskar solvärmen. Säkerhetsfilmen håller ihop glaset vid slag och krossning. Samtidigt minskar den bländningen och blockerar 99 % av UV-strålningen.",
            "Du får båda funktionerna med en gemensam garanti. Det ger lägre totalkostnad och mindre stillestånd i verksamheten än att montera solfilm och säkerhetsfilm var för sig.",
          ],
          bullets: [
            "Skydd mot inbrott och splitter (EN 12600)",
            "Värmereduktion upp till 80 %",
            "UV-blockering 99 % — skyddar inredning och färger",
            "Bländningsreduktion för bättre inomhusmiljö",
            "Solskydd och säkerhetsfilm monteras samtidigt",
          ],
          image: BBN_SECURITY[7],
        },
        {
          eyebrow: "SORTIMENT",
          heading: "3M Safety S40 & Safety S70 med solreduktion.",
          bullets: [
            "3M Safety S40 — mörkare toning, hög värmereduktion",
            "3M Safety S70 — ljusare toning, hög ljustransmission",
            "Båda finns för invändig och utvändig montering",
            "Optiskt neutral färg — påverkar inte fasadens uttryck",
          ],
          extra: (
            <div className="flex flex-wrap gap-3">
              <a href={PDF_40} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-3 text-sm font-semibold hover:bg-navy/90 transition-colors">
                Datablad Safety S40 (PDF) →
              </a>
              <a href={PDF_70} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-3 text-sm font-semibold hover:bg-navy/90 transition-colors">
                Datablad Safety S70 (PDF) →
              </a>
            </div>
          ),
          image: IMAGES.installer,
        },
        {
          eyebrow: "TYPISKA ANVÄNDNINGSOMRÅDEN",
          heading: "Där båda behoven finns samtidigt.",
          bullets: [
            "Butiksskyltfönster mot söder — värme + inbrottsrisk",
            "Advokat- och konsultkontor med stora exponerade fönster",
            "Bilhallars showroom-glas — solljus + värdefulla objekt",
            "Restauranger och caféer med sittplatser i solläge och utsatt läge",
          ],
        },
      ]}
      related={[
        { to: "/3m-ultra-sakerhetsfilm", label: "3M Ultra", desc: "När maximalt inbrottsskydd krävs." },
        { to: "/3m-sakerhetsfilm-utan-solfilm", label: "Klar säkerhetsfilm", desc: "Utan tonad optik." },
        { to: "/sakerhetsfilm", label: "Säkerhetsfilm — översikt", desc: "Se de olika säkerhetsfilmerna." },
      ]}
    >
      <section className="mx-auto max-w-6xl px-5 md:px-8 py-10">
        <h2 className="font-display text-2xl md:text-3xl text-foreground mb-5">3M Ultra — säkerhetsfilm med solfilm</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {ULTRA_COMBI.map((p) => (
            <article key={p.name} className="rounded-2xl border border-gold/15 bg-card/60 p-5 flex flex-col">
              <h3 className="font-display text-lg text-foreground mb-3">{p.name}</h3>
              <ul className="text-sm text-muted-foreground space-y-1.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2"><span className="text-gold mt-1">·</span><span>{f}</span></li>
                ))}
              </ul>
              {p.pdf && (
                <a href={p.pdf} target="_blank" rel="noopener" className="mt-4 text-sm text-gold-bright underline underline-offset-2 hover:text-gold">
                  Hämta datablad (.PDF)
                </a>
              )}
            </article>
          ))}
        </div>
      </section>
      <FaqSection heading="Vanliga frågor om kombifilm." items={FAQS} />
      
      <CallbackRail product="Säkerhetsfilm" />
    </ContentPage>
  ),
});
